import { useMutation } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { postUserLangApi } from '@flipster/apis/v1/user';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { userRegulationBunja } from '@flipster/features/src/shared/regulation';
import { languageLabels, supportedLocaleToLanguageTag } from '@flipster/i18n/constants';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { usePathname } from '@flipster/i18n/routing';
import { SupportedLocale } from '@flipster/i18n/types';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { getErrorMessage } from '@flipster/utils/error';

interface LanguageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

const getDefaultLanguage = (isVaraRegulated: boolean, locale: SupportedLocale): SupportedLocale => {
  if (isVaraRegulated) return 'en-AE';
  if (locale === 'en-AE') return 'en';
  return locale;
};

export const LanguageModal = ({ isOpen, onClose, onExit }: LanguageModalProps) => {
  const transCommon = useTranslations('Common.r250401');
  const transLanguageSetting = useTranslations('Language_setting.r250401');
  const pathname = usePathname();
  const locale = useSupportedLocale();

  const { isVaraRegulatedAtom } = useBunja(userRegulationBunja);
  const isVaraRegulated = useAtomValue(isVaraRegulatedAtom);

  const [language, setLanguage] = useState<SupportedLocale>(getDefaultLanguage(isVaraRegulated, locale));

  useEffect(() => {
    setLanguage(getDefaultLanguage(isVaraRegulated, locale));
  }, [isVaraRegulated, locale]);

  const { isLoggedIn, getMe } = useAuth();

  const { mutateAsync } = useMutation({
    mutationKey: ['postUserLangApi'],
    mutationFn: postUserLangApi,
  });

  const handleSetLanguage = (lang: SupportedLocale) => {
    // VARA users can only select en-AE
    if (isVaraRegulated) {
      setLanguage('en-AE');
      return;
    }
    // Non-VARA users cannot select en-AE
    if (lang === 'en-AE') return;
    setLanguage(lang);
  };

  const onConfirm = async () => {
    try {
      if (isLoggedIn) {
        await mutateAsync(supportedLocaleToLanguageTag[language]);
        // 서버의 사용자 언어가 갱신되도록 즉시 재조회하여 UserLanguageRedirect가 이전 언어로 되돌리는 것을 방지
        await getMe();
      }
      const prefix = `/${language}`;
      // 새로고침 하지 않으면 언어 변경이 제대로 적용되지 않는 이슈가 있음
      window.location.replace(`${prefix}${pathname}`);
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
    } finally {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} size='small' onExit={onExit}>
      <Modal.Close onClose={onClose} />
      <Modal.Header>
        <Modal.Title label={transLanguageSetting('Language_setting_title')} />
      </Modal.Header>
      <Modal.Content>
        <p className={css({ textStyle: 'Text/M/Regular', mb: 12 })}>
          We're improving our translations. Some text may feel unnatural.
        </p>
        <Message language={language} setLanguage={handleSetLanguage} isVaraRegulated={isVaraRegulated} />
      </Modal.Content>
      <Modal.Footer>
        <Button onClick={onConfirm} label={transCommon('Confirm')} />
      </Modal.Footer>
    </Modal>
  );
};

const languageOptions = [
  {
    label: languageLabels.en,
    value: 'en',
  },
  {
    label: languageLabels.vi,
    value: 'vi',
    description:
      'Chúng tôi đang trong quá trình cải thiện chất lượng bản dịch. Một số nội dung có thể chưa được diễn đạt chính xác.',
  },
  {
    label: languageLabels['zh-CN'],
    value: 'zh-CN',
    description: '我们的翻译还在持续优化中，部分表述可能略显生硬，请见谅。',
  },
  {
    label: languageLabels['zh-TW'],
    value: 'zh-TW',
    description: '我們正持續優化翻譯品質，部分內容可能仍在調整中，感謝你的理解。',
  },
  {
    label: languageLabels.ru,
    value: 'ru',
    description: 'Мы работаем над улучшением нашего сайта. Переведенная версия может содержать неточности.',
  },
  {
    label: languageLabels.ja,
    value: 'ja',
    description: '翻訳の品質向上に取り組んでいます。一部の表現が不自然に感じられる場合があります。',
  },
  {
    label: languageLabels['en-AE'],
    value: 'en-AE',
    isBeta: true,
    description: 'English is currently the only supported language for users under UAE regulations.',
  },
];

const Message = ({
  language,
  setLanguage,
  isVaraRegulated,
}: {
  language: SupportedLocale;
  setLanguage: (language: SupportedLocale) => void;
  isVaraRegulated: boolean;
}) => {
  return (
    <fieldset className={css({ display: 'flex', flexDirection: 'column' })}>
      {languageOptions.map((item) => {
        const isEnAE = item.value === 'en-AE';
        const isDisabled = isVaraRegulated ? !isEnAE : isEnAE;

        return (
          <div key={item.value}>
            <RadioButton
              key={item.value}
              name='language'
              value={item.value}
              checked={language === item.value}
              disabled={isDisabled}
              onChange={(e) => setLanguage(e.target.value as SupportedLocale)}
              css={{ color: 'content.default.level-5', py: 12 }}
            >
              <span className={hstack({ gap: 8 })}>{item.label}</span>
            </RadioButton>
            {language === item.value && <p className={css({ textStyle: 'Text/M/Regular' })}>{item.description}</p>}
          </div>
        );
      })}
    </fieldset>
  );
};
